import {
    CadFeatureMap,
    RequestAssistanceForm as RequestAssistanceFormType,
    RequestAssistanceView,
} from '@mark43/cad-api';
import {
    ButtonGroup,
    Flex,
    Icon,
    FormGroup,
    Stack,
    Box,
    Button,
    Card,
    CardBody,
    CloseButton,
    Text,
    FormRow,
} from '@cad-arc';
import { FormInput } from '../../forms/integrated-form-controls/form-input';
import {
    FormLocationSelect,
    LocationFormRowWrapper,
} from '../../forms/integrated-form-controls/form-location-select';
import {
    Controller,
    useFieldArray,
    useFormContext,
    UseFormReturn,
    useWatch,
} from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { AgencyTypeGlobal, globalAttributes } from '../../common/global-attributes';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import {
    RequestAssistanceFormDisplayType,
    RequestAssistanceOptions,
    IntialAssistanceAgencyType,
} from '../types';
import { FormEditable } from '../../forms/integrated-form-components/form-editable';
import { RequestAssistanceSubdivisionFields } from './request-assistance-subdivision-fields';
import { AgencyFieldsDisplayRow } from '../../panels/entity-panels/entity-panel-components';
import { useAgencyOptions } from '../../event-panel/event-panel-form/use-agency-options';
import { agencyTypeDisplayNames, agencyTypeIconNames } from '../../common/helpers/agency-type';
import {
    useLocationPrefill,
    UseLocationPrefillArgs,
} from '../../location-select/use-location-prefill';
import { useRequestAssistancePrefillOnFormAdd } from '../hooks/use-request-assistance-form-prefill';
import { ClassificationStrategy } from '../../common/types';
import { CopyOptionsForm } from '~/copy-call/components/copy-options-form';
interface RequestAssistanceFormProps {
    formData: RequestAssistanceView;
    initialAssistanceAgencyTypes?: IntialAssistanceAgencyType[];
}

function LocationRelatedFields({
    asDataListItem,
    agencyType,
    form,
    index,
    options,
    enabledFeatures,
    onBlur,
}: {
    agencyType: AgencyTypeGlobal;
    asDataListItem?: boolean;
    index: number;
    enabledFeatures: CadFeatureMap;
    options: RequestAssistanceOptions[number];
    form: UseFormReturn<RequestAssistanceFormDisplayType>;
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
}): JSX.Element {
    const { control } = form;

    const location = useWatch({
        control,
        name: `forms.${index}.consolidatedLocationView`,
    });

    const agencyOptions = useAgencyOptions({
        baseOptions: options.agencyProfileOptions,
        location,
        agencyType,
    });

    return asDataListItem ? (
        <Stack spacing={0}>
            <AgencyFieldsDisplayRow>
                <RequestAssistanceSubdivisionFields
                    eventClassPrefix={
                        agencyType === globalAttributes.agencyTypeGlobal.FIRE
                            ? 'fire'
                            : agencyType === globalAttributes.agencyTypeGlobal.EMS
                            ? 'ems'
                            : 'police'
                    }
                    formIndex={index}
                    form={form}
                    asDataListItem={true}
                />
            </AgencyFieldsDisplayRow>
            <AgencyFieldsDisplayRow>
                <FormSelect
                    asDataListItem={true}
                    asEditable={false}
                    control={control}
                    label="D-Area"
                    name={`forms.${index}.dispatchAreaOption`}
                    options={options.dispatchAreaOptions}
                    rules={{ required: 'Required' }}
                    length="sm"
                />
                <FormSelect
                    control={control}
                    asDataListItem={true}
                    asEditable={false}
                    label="Agency"
                    rules={{ required: 'Required' }}
                    name={`forms.${index}.agencyProfileOption`}
                    options={agencyOptions}
                    length="md"
                />
            </AgencyFieldsDisplayRow>
            {enabledFeatures.radioChannels ? (
                <AgencyFieldsDisplayRow>
                    <FormSelect
                        asDataListItem={true}
                        asEditable={false}
                        control={control}
                        label="Radio Channel"
                        name={`forms.${index}.radioChannelOption`}
                        options={options.radioChannelOptions}
                        length="md"
                    />
                </AgencyFieldsDisplayRow>
            ) : undefined}
        </Stack>
    ) : (
        <FormGroup onBlur={onBlur} spacing={1}>
            <FormRow>
                <RequestAssistanceSubdivisionFields
                    eventClassPrefix={
                        agencyType === globalAttributes.agencyTypeGlobal.FIRE
                            ? 'fire'
                            : agencyType === globalAttributes.agencyTypeGlobal.EMS
                            ? 'ems'
                            : 'police'
                    }
                    formIndex={index}
                    form={form}
                    asDataListItem={false}
                />
            </FormRow>
            <FormRow>
                <FormSelect
                    asDataListItem={false}
                    asEditable={false}
                    control={control}
                    label="D-Area"
                    name={`forms.${index}.dispatchAreaOption`}
                    options={options.dispatchAreaOptions}
                    rules={{ required: 'Required' }}
                    length="sm"
                />
                <FormSelect
                    control={control}
                    asDataListItem={false}
                    asEditable={false}
                    label="Agency"
                    rules={{ required: 'Required' }}
                    name={`forms.${index}.agencyProfileOption`}
                    options={agencyOptions}
                    length="md"
                />
            </FormRow>
            {enabledFeatures.radioChannels ? (
                <FormSelect
                    asDataListItem={asDataListItem}
                    asEditable={false}
                    control={control}
                    label="Radio Channel"
                    name={`forms.${index}.radioChannelOption`}
                    options={options.radioChannelOptions}
                    length="md"
                />
            ) : undefined}
        </FormGroup>
    );
}

function RequestAssistanceFormFields({
    form,
    index,
    options,
    agencyType,
}: {
    index: number;
    agencyType: AgencyTypeGlobal;
    options: RequestAssistanceOptions[number];
    form: UseFormReturn<RequestAssistanceFormDisplayType>;
}): JSX.Element {
    const enabledFeatures = useEnabledFeatures();

    function handleBlur(event: React.FocusEvent<HTMLDivElement>, toggleMode?: () => void) {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            toggleMode?.();
        }
    }

    const locationPrefillArgs: UseLocationPrefillArgs<RequestAssistanceFormDisplayType> =
        useMemo(() => {
            const args: UseLocationPrefillArgs<RequestAssistanceFormDisplayType> = {
                agencyType,
                form,
                fieldPaths: {
                    location: `forms.${index}.consolidatedLocationView`,
                    dispatchArea: `forms.${index}.dispatchAreaOption`,
                    agency: `forms.${index}.agencyProfileOption`,
                    radioChannel: `forms.${index}.radioChannelOption`,
                    subdivision1: `forms.${index}.subdivision1`,
                    subdivision2: `forms.${index}.subdivision2`,
                    subdivision3: `forms.${index}.subdivision3`,
                    subdivision4: `forms.${index}.subdivision4`,
                    subdivision5: `forms.${index}.subdivision5`,
                },
                options: {
                    dispatchAreaOptions: options.dispatchAreaOptions,
                    agencyProfileOptions: options.agencyProfileOptions,
                    radioChannelOptions: options.radioChannelOptions,
                },
            };
            return args;
        }, [
            agencyType,
            form,
            index,
            options.agencyProfileOptions,
            options.dispatchAreaOptions,
            options.radioChannelOptions,
        ]);

    const observerCallback =
        useLocationPrefill<RequestAssistanceFormDisplayType>(locationPrefillArgs);
    useRequestAssistancePrefillOnFormAdd({ form, observerCallback });

    const { control } = form;
    const subdivisionProps = {
        enabledFeatures,
        options,
        agencyType,
        form,
        index,
    };
    return (
        <>
            <LocationFormRowWrapper subpremisesEnabled={enabledFeatures.subpremiseSupport}>
                <FormLocationSelect
                    asEditable={!enabledFeatures.subpremiseSupport}
                    name={`forms.${index}.consolidatedLocationView`}
                    control={control}
                    rules={{ required: 'Required' }}
                    label="Location"
                    classificationStrategy={
                        enabledFeatures.multiAgencySubdivisions
                            ? ClassificationStrategy.MULTI_AGENCY
                            : ClassificationStrategy.SINGLE_AGENCY
                    }
                    length="lg"
                />

                {!enabledFeatures.subpremiseSupport && (
                    <FormInput
                        asEditable={true}
                        name={`forms.${index}.subPremise`}
                        control={control}
                        label="Apt, Unit"
                        length="sm"
                    />
                )}
            </LocationFormRowWrapper>
            <FormSelect
                asEditable={true}
                control={control}
                label="Call For Service"
                name={`forms.${index}.callForServiceOption`}
                rules={{ required: 'Required' }}
                options={options.callForServiceOptions}
            />
            <FormInput
                asEditable={true}
                name={`forms.${index}.notes`}
                control={control}
                label="Notes"
            />
            <FormEditable
                style={{ marginTop: 'var(--arc-spaces-2)' }}
                viewMode={(containerProps) => (
                    <span {...containerProps}>
                        <LocationRelatedFields {...subdivisionProps} asDataListItem={true} />
                    </span>
                )}
                editMode={({ toggleMode }) => (
                    <LocationRelatedFields
                        {...subdivisionProps}
                        onBlur={(e) => handleBlur(e, toggleMode)}
                    />
                )}
            />
        </>
    );
}

function isButtonDisabled({
    forms,
    initialForms,
    agencyType,
    eventAgencyTypeGlobalAttrId,
}: {
    forms: RequestAssistanceFormType[];
    initialForms: RequestAssistanceFormType[];
    eventAgencyTypeGlobalAttrId: number;
    agencyType: number;
}): boolean {
    return (
        initialForms.findIndex((form) => form.agencyTypeGlobalAttrId === agencyType) === -1 ||
        eventAgencyTypeGlobalAttrId === agencyType ||
        forms.findIndex((field) => field.agencyTypeGlobalAttrId === agencyType) > -1
    );
}

export function RequestAssistanceForm({
    formData,
    initialAssistanceAgencyTypes,
}: RequestAssistanceFormProps): JSX.Element {
    const { requestAssistanceForms, requestAssistanceStatics, requestAssistanceSelections } =
        formData;
    const form = useFormContext<RequestAssistanceFormDisplayType>();
    const { control, reset } = form;
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'forms',
        rules: { minLength: 1, required: 'Required' },
    });

    useEffect(
        function hydrateForm() {
            if (requestAssistanceForms) {
                const { useCurrentDateTime, copyAllInformation, copyAttachments } = requestAssistanceSelections;
                const initialForms = initialAssistanceAgencyTypes
                    ? requestAssistanceForms.filter((form) =>
                          initialAssistanceAgencyTypes.includes(
                              form.agencyTypeGlobalAttrId as IntialAssistanceAgencyType
                          )
                      )
                    : [];
                reset(
                    {
                        useCurrentDateTime: useCurrentDateTime ? 'true' : 'false',
                        copyAllInformation,
                        copyAttachments,
                        forms: initialForms,
                    },
                    {
                        keepDefaultValues: true,
                    }
                );
            }
        },
        [requestAssistanceForms, initialAssistanceAgencyTypes, requestAssistanceSelections, reset]
    );

    const optionsByAgencyType = useMemo(
        () =>
            requestAssistanceForms.reduce<RequestAssistanceOptions>((acc, item) => {
                acc[item.agencyTypeGlobalAttrId] = {
                    agencyProfileOptions: item.agencyProfileOptions,
                    callForServiceOptions: item.callForServiceOptions,
                    dispatchAreaOptions: item.dispatchAreaOptions,
                    radioChannelOptions: item.radioChannelOptions,
                };
                return acc;
            }, {}),
        [requestAssistanceForms]
    );

    return (
        <Flex direction="column" gap={4}>
            <Box zIndex="1" bg="surface.foreground">
                <Flex p={4} bg="surface.accent" justifyContent="space-between" alignItems="center" borderRadius="md">
                    <Text variant="headingXs" casing="uppercase">
                        Select Class(es)
                    </Text>
                    <ButtonGroup>
                        {Object.values(globalAttributes.agencyTypeGlobal).map(
                            (agencyTypeGlobalAttrId) => {
                                const agencyType = agencyTypeGlobalAttrId;
                                const icon = agencyTypeIconNames[agencyTypeGlobalAttrId];
                                const title = agencyTypeDisplayNames[agencyTypeGlobalAttrId];
                                const isDisabled = isButtonDisabled({
                                    agencyType,
                                    initialForms: requestAssistanceForms,
                                    eventAgencyTypeGlobalAttrId:
                                        requestAssistanceStatics.agencyTypeGlobalAttrId,
                                    forms: fields,
                                });
                                return (
                                    <Button
                                        key={title}
                                        variant={isDisabled ? 'outline' : 'solid'}
                                        leftIcon={icon}
                                        isDisabled={isDisabled}
                                        onClick={() => {
                                            const formToAdd = requestAssistanceForms.find(
                                                (initialForm) =>
                                                    initialForm.agencyTypeGlobalAttrId ===
                                                    agencyTypeGlobalAttrId
                                            );
                                            if (formToAdd) {
                                                append({
                                                    ...formToAdd,
                                                    notes: '',
                                                });
                                            }
                                        }}
                                    >
                                        {title}
                                    </Button>
                                );
                            }
                        )}
                    </ButtonGroup>
                </Flex>
            </Box>
            <Controller
                control={control}
                name="forms"
                render={() => {
                    if (!fields.length) {
                        return (
                            <Flex justifyContent="center" padding={16}>
                                <Text variant="headingXs" color="text.placeholder">
                                    No classes selected
                                </Text>
                            </Flex>
                        );
                    }
                    return (
                        <>
                            {fields.map((field, index) => {
                                const icon =
                                    agencyTypeIconNames[
                                        field.agencyTypeGlobalAttrId as AgencyTypeGlobal
                                    ];
                                const title =
                                    agencyTypeDisplayNames[
                                        field.agencyTypeGlobalAttrId as AgencyTypeGlobal
                                    ];
                                return (
                                    <Card key={field.id}>
                                        <CardBody>
                                            <Flex
                                                justifyContent="space-between"
                                                lineHeight={1}
                                                alignItems="center"
                                            >
                                                <Text>
                                                    <Icon
                                                        icon={icon}
                                                        style={{ marginRight: '.5em' }}
                                                        data-testid={`REQUEST_ASSISTANCE_ICON_${icon}`}
                                                    />
                                                    {title}
                                                </Text>
                                                <CloseButton
                                                    aria-label="Remove"
                                                    onClick={() => remove(index)}
                                                />
                                            </Flex>
                                            <RequestAssistanceFormFields
                                                form={form}
                                                index={index}
                                                options={
                                                    optionsByAgencyType[
                                                        field.agencyTypeGlobalAttrId
                                                    ]
                                                }
                                                agencyType={
                                                    field.agencyTypeGlobalAttrId as AgencyTypeGlobal
                                                }
                                            />
                                        </CardBody>
                                    </Card>
                                );
                            })}
                        </>
                    );
                }}
            />

            <CopyOptionsForm control={control} />
        </Flex>
    );
}
